import { Helmet } from 'react-helmet-async';
import { INSTAGRAM_URL } from '../constants/social';

/** Primary public URL (custom domain on Firebase Hosting). */
const SITE_URL = 'https://bodyworks.training';
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

const DEFAULT_TITLE =
  'BodyWorks Training | Personal Trainer Palmetto Bay, Pinecrest & Kendall — Mobile & Group';
const DEFAULT_DESCRIPTION =
  'Looking for a personal trainer near you? BodyWorks Training (Bodyworks Training LLC) serves Palmetto Bay, Pinecrest & Kendall with equipped mobile personal training, group fitness classes, and private coaching. Weight loss, strength, sports performance & online options across Miami-Dade.';

function SEOHead({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  canonical = `${SITE_URL}/`,
  type = 'website',
  image = DEFAULT_IMAGE,
  keywords = 'personal trainer near me, personal trainer Palmetto Bay, personal trainer Pinecrest, personal trainer Kendall, mobile personal training Palmetto Bay, mobile personal training Pinecrest, mobile personal training Kendall, group fitness classes Palmetto Bay, group fitness classes Pinecrest, group fitness classes Kendall, BodyWorks Training, Bodyworks Training LLC, mobile personal training Miami, equipped mobile training, fitness coach South Florida',
}) {
  const organizationId = `${SITE_URL}/#organization`;
  const websiteId = `${SITE_URL}/#website`;
  const localBusinessId = `${SITE_URL}/#localbusiness`;

  const structuredDataGraph = {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            '@id': organizationId,
            name: 'BodyWorks Training',
            legalName: 'Bodyworks Training LLC',
            alternateName: [
              'BodyWorks Personal Training',
              'Bodyworks Training Miami',
              'BodyWorks Training Florida',
            ],
            url: `${SITE_URL}/`,
            logo: {
              '@type': 'ImageObject',
              url: DEFAULT_IMAGE,
            },
            description,
            sameAs: [
              INSTAGRAM_URL,
              'https://www.facebook.com/carlotraining',
            ],
          },
          {
            '@type': 'WebSite',
            '@id': websiteId,
            url: `${SITE_URL}/`,
            name: 'BodyWorks Training',
            publisher: { '@id': organizationId },
            inLanguage: 'en-US',
          },
          {
            '@type': 'LocalBusiness',
            '@id': localBusinessId,
            name: 'BodyWorks Training',
            parentOrganization: { '@id': organizationId },
            image,
            description,
            url: `${SITE_URL}/`,
            priceRange: '$$',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Pinecrest Area',
              addressLocality: 'Pinecrest',
              addressRegion: 'FL',
              postalCode: '33156',
              addressCountry: 'US',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: 25.6654,
              longitude: -80.3058,
            },
            areaServed: [
              { '@type': 'City', name: 'Palmetto Bay' },
              { '@type': 'City', name: 'Pinecrest' },
              { '@type': 'City', name: 'Kendall' },
              { '@type': 'City', name: 'South Miami' },
              { '@type': 'City', name: 'Coral Gables' },
            ],
            openingHoursSpecification: [
              {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                opens: '06:00',
                closes: '20:00',
              },
              {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: 'Saturday',
                opens: '07:00',
                closes: '18:00',
              },
            ],
            sameAs: [
              INSTAGRAM_URL,
              'https://www.facebook.com/carlotraining',
            ],
          },
          {
            '@type': 'Service',
            '@id': `${SITE_URL}/#service-mobile-training`,
            name: 'Mobile personal training',
            serviceType: 'Personal trainer',
            provider: { '@id': organizationId },
            description:
              'Equipped mobile personal training at your home or location in Palmetto Bay, Pinecrest, Kendall and nearby Miami-Dade. Private sessions for weight loss, strength, and sports performance.',
            areaServed: {
              '@type': 'GeoCircle',
              geoMidpoint: {
                '@type': 'GeoCoordinates',
                latitude: 25.6654,
                longitude: -80.3058,
              },
              geoRadius: '20000',
            },
          },
          {
            '@type': 'Service',
            '@id': `${SITE_URL}/#service-group-fitness`,
            name: 'Group fitness classes',
            serviceType: 'Group fitness',
            provider: { '@id': organizationId },
            description:
              'Small group fitness classes and team training in Palmetto Bay, Pinecrest, Kendall and surrounding areas. Strength, conditioning, and accountability in a coached group setting.',
            areaServed: {
              '@type': 'GeoCircle',
              geoMidpoint: {
                '@type': 'GeoCoordinates',
                latitude: 25.6654,
                longitude: -80.3058,
              },
              geoRadius: '20000',
            },
          },
          {
            '@type': 'ItemList',
            name: 'Training locations & service area',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                item: {
                  '@type': 'Place',
                  name: 'Lifetime Fitness Pinecrest area',
                  address: {
                    '@type': 'PostalAddress',
                    streetAddress: '12451 N Kendall Dr',
                    addressLocality: 'Miami',
                    addressRegion: 'FL',
                    postalCode: '33186',
                  },
                },
              },
              {
                '@type': 'ListItem',
                position: 2,
                item: {
                  '@type': 'Place',
                  name: 'Pinecrest Community Center',
                  address: {
                    '@type': 'PostalAddress',
                    streetAddress: '5855 SW 111th St',
                    addressLocality: 'Pinecrest',
                    addressRegion: 'FL',
                    postalCode: '33156',
                  },
                },
              },
              {
                '@type': 'ListItem',
                position: 3,
                item: {
                  '@type': 'Place',
                  name: 'Palmetto Bay Community Center',
                  address: {
                    '@type': 'PostalAddress',
                    streetAddress: '17641 Old Cutler Rd',
                    addressLocality: 'Palmetto Bay',
                    addressRegion: 'FL',
                    postalCode: '33157',
                  },
                },
              },
            ],
          },
        ],
      };

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <link rel="canonical" href={canonical} />

      <meta name="geo.region" content="US-FL" />
      <meta name="geo.placename" content="Palmetto Bay, Pinecrest, Kendall, Florida" />
      <meta name="geo.position" content="25.6654;-80.3058" />
      <meta name="ICBM" content="25.6654, -80.3058" />

      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="BodyWorks Training" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <meta property="business:contact_data:locality" content="Pinecrest" />
      <meta property="business:contact_data:region" content="Florida" />
      <meta property="business:contact_data:postal_code" content="33156" />
      <meta property="business:contact_data:country_name" content="USA" />

      <script type="application/ld+json">{JSON.stringify(structuredDataGraph)}</script>
    </Helmet>
  );
}

export default SEOHead;
export { SITE_URL, DEFAULT_TITLE, DEFAULT_DESCRIPTION, DEFAULT_IMAGE };
